import React, { memo, useEffect, useMemo, useState } from 'react';
import { every, get, isEmpty, isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DropdownCustom } from '..';
import { updateApproveSuccessionPlanningOptions, updateOtherLoading } from '../../pages/TalentProfilePage/store/printProfileSlice';
import { updateDownloadApproveSuccessionPlanningOptions } from '../../pages/TalentProfilePage/store/downloadProfileSlice';
import { fetchApprovedSP } from '../../services/talentProfiles';
import CommonLoading from '../CommonLoading/CommonLoading';
import styles from './approved-sp.module.scss';
import ApprovedSpItem from './ApprovedSpItem';

const optionApprovedSP = [
  {
    value: 'All',
    label: 'All',
  },
];

const initApprovedSP = {
  VP: null,
  SGM: null,
  GM: null,
  SM: null,
  Mgr: null,
  Exec: null,
};

const ORDER_ROW = ['VP', 'SGM', 'GM', 'SM', 'Mgr', 'Exec'];

const getNewOptions = (res, key) => get(res, key);

const buildOptions = (baseOptions, newItems) =>
  isEmpty(newItems)
    ? baseOptions
    : [
        ...baseOptions,
        ...ORDER_ROW.filter((item) => newItems.includes(item)).map((item) => ({
          value: item,
          label: item,
        })),
      ];

const ApprovedSP = ({ id, roleUser, isPrintTP, tokenTalentProfile, isDownloadPreview }) => {
  const [approvedSp, setApprovedSp] = useState(initApprovedSP);
  const [dropdownOptions, setDropdownOptions] = useState(optionApprovedSP);
  const [loading, setLoading] = useState(false);
  const [selectedApprovedSP, setSelectedApprovedSP] = useState(optionApprovedSP[0]);
  const { approved_succession_planning } = useSelector((state) => isDownloadPreview ? state.app.talentProfile.downloadProfile.downloadSettings.radioOptions : state.app.talentProfile.printProfile.printSettings.radioOptions);

  const dispatch = useDispatch();

  const isAll = useMemo(() => {
    return selectedApprovedSP.value === optionApprovedSP[0].value;
  }, [selectedApprovedSP.value]);

  const getApprovedSP = async () => {
    setLoading(true);
    dispatch(updateOtherLoading({ fieldName: 'approvedSp', value: true }));
    try {
      let res = await fetchApprovedSP({
        id,
        selectFilter: selectedApprovedSP.value,
        roleUser,
        tokenTalentProfile,
      });
      const resResult = get(res, 'data.result.data');
      const sortedResult = {};

      if (!isEmpty(resResult)) {
        ORDER_ROW.forEach((key) => {
          // eslint-disable-next-line no-prototype-builtins
          if (resResult?.hasOwnProperty(key)) {
            sortedResult[key] = resResult[key];
          }
        });
        setApprovedSp(sortedResult);
      } else {
        setApprovedSp(initApprovedSP);
      }

      dispatch(updateApproveSuccessionPlanningOptions(buildOptions(optionApprovedSP, getNewOptions(res, 'data.result.roleAccessLevels'))));
      dispatch(updateDownloadApproveSuccessionPlanningOptions(buildOptions(optionApprovedSP, getNewOptions(res, 'data.result.roleAccessLevels'))));
      setDropdownOptions(buildOptions(optionApprovedSP, getNewOptions(res, 'data.result.roles')));
    } catch (error) {
      setApprovedSp([]);
      console.error(error);
    } finally {
      setLoading(false);
      dispatch(updateOtherLoading({ fieldName: 'approvedSp', value: false }));
    }
  };

  useEffect(() => {
    if (!roleUser.roleActive.roleId || !id) return;
    getApprovedSP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleUser.roleActive.roleId, id, selectedApprovedSP.value]);

  useEffect(() => {
    if (!id) return;
    setSelectedApprovedSP(optionApprovedSP[0]);
  }, [id]);

  useEffect(() => {
    if (!isPrintTP) return;
    setSelectedApprovedSP({ label: approved_succession_planning, value: approved_succession_planning });
  }, [approved_succession_planning, isPrintTP]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.label}>Approved Succession Planning </h3>
        {!isPrintTP && (
          <DropdownCustom
            options={dropdownOptions}
            selected={selectedApprovedSP}
            setSelected={setSelectedApprovedSP}
            maxWidth="79px"
            maxHeight="40px"
            topbottom="11px"
            leftright="16px"
            space="13px"
            shadow="false"
          />
        )}
      </div>
      <div className={styles.milestone}>
        {!loading ? (
          <div className={styles.table}>
            <div className={styles.table_header}>
              {isAll && !every(approvedSp, isNull) && <div className={styles.role} />}
              <div className={styles.line_title}>
                <p>1st Line</p>
                <p>0 - 3 years</p>
              </div>
              <div className={styles.line_title}>
                <p>2nd Line</p>
                <p>3 - 5 years</p>
              </div>
              <div className={styles.line_title}>
                <p>3rd Line</p>
                <p>{'>'} 5 years</p>
              </div>
            </div>
            <div className={styles.table_data}>
              {approvedSp &&
                Object.keys(approvedSp).map((key) => {
                  const role = approvedSp[key];
                  return (
                    <React.Fragment key={key}>
                      {role && (
                        <div key={key} className={styles.row_data}>
                          {isAll && <div className={styles.cell_key}>{key}</div>}
                          <div className={styles.cell_data}>
                            <ApprovedSpItem isPrint={isPrintTP} line={role.first} />
                          </div>
                          <div className={styles.cell_data}>
                            <ApprovedSpItem isPrint={isPrintTP} line={role.second} />
                          </div>
                          <div className={styles.cell_data}>
                            <ApprovedSpItem isPrint={isPrintTP} line={role.third} />
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        ) : (
          <div style={{ width: '100%' }} className="d-flex justify-content-center">
            <CommonLoading />
          </div>
        )}
      </div>
    </div>
  );
};

ApprovedSP.propTypes = {
  id: PropTypes.string,
  roleUser: PropTypes.object,
  isPrintTP: PropTypes.bool,
};

export default memo(ApprovedSP);
